import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import { MDText } from 'i18n-react';

import Tree from './Tree';
import './_DropdownTree.scss';
import SearchBox from '@adsk/bim360-matrix-react-search-box';
import { listTruncationWithOverflow } from '@adsk/bim360-matrix-react-helpers/es/listTruncation';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';
/**
 * A Dropdown Tree component
 */
export class DropdownTree extends React.Component {
  /**
   * constructor
   */
  constructor(props) {
    super(props);
    this.TEMP_ROOT_ID = 'TEMP_ROOT';
    this.prepareStateWithTree = this.prepareStateWithTree.bind(this);

    const state = this.prepareStateWithTree(
      props.tree,
      props.selectedEventKey,
      props.expandRoot,
      '',
      true
    );
    this.state = {
      ...state,
      isHovered: false,
      isOpen: props.openOnMount && !props.readOnlyValue
    };

    this._handleOnFocus = this._handleOnFocus.bind(this);
    this._handleOnSelect = this._handleOnSelect.bind(this);
    this._handleClearSearch = this._handleClearSearch.bind(this);
    this._handleSearchBoxOnChange = this._handleSearchBoxOnChange.bind(this);
    this._searchTree = this._searchTree.bind(this);
    this._debouncedSearchTree = debounce(this._searchTree, 500);
    // This bind is needed for react-onclickoutside to correctly close a menu when a user clicks outside of a component
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this._handleCancel = this._handleCancel.bind(this);
    this._onMouseOverPopover = this._onMouseOverPopover.bind(this);
    this._onMouseOutPopover = this._onMouseOutPopover.bind(this);
    this.getInputValue = this.getInputValue.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.tree !== this.props.tree ||
      nextProps.selectedEventKey !== this.props.selectedEventKey
    ) {
      this.prepareStateWithTree(nextProps.tree, nextProps.selectedEventKey, nextProps.expandRoot);
    }
    if (this.props.availableWidth !== nextProps.availableWidth && nextProps.availableWidth) {
      this.setState({ width: nextProps.availableWidth });
    }
  }

  componentDidMount() {
    this.resizeObserver = new ResizeObserver((entries, observer) => {
      const entry = entries[0];
      if (entry) {
        const currentWidth = entry.contentRect.width;
        if (this.state.width !== currentWidth) {
          this.setState({ width: currentWidth });
        }
      }
    });

    if (this.props.availableWidth) {
      this.setState({ width: this.props.availableWidth });
    } else {
      const elementToObserve = this.props.readOnlyValue
        ? this.readOnlyLabelElement
        : this.inputElement;
      this.resizeObserver.observe(elementToObserve);
    }
  }

  /**
   * componentDidUpdate
   */
  componentDidUpdate(prevProps, prevState) {
    if (prevState.searchExpression !== this.state.searchExpression) {
      if (!this.state.searchExpression) {
        this._searchTree();
      } else {
        this._debouncedSearchTree();
      }
    }
  }

  componentWillUnmount() {
    this.resizeObserver.disconnect();
  }

  /**
   * handle click outside the drop down tree.
   */
  handleClickOutside() {
    this.setState({ open: false });
    this.props.onClickOutside && this.props.onClickOutside();
  }

  /***
   * Recursively prepare tree for react-treebeard.
   * We need to go over all of the nodes to set 'ancestors' array for every node.
   * In case when searchExpression is not empty - find all nodes that match the search. set toggle to true for every parent. remove nodes which do not have a child or ancestor that matches search.
   * In case when searchExpression is empty - find selectedKey node in the tree -> set its 'active' flag to 'true', set all its ancestors 'toggled' value to true.
   * @param currNode - the current node we traverse
   * @param selectedKey - the selected key we're looking for
   * @param ancestors - array of ancestors of current node
   * @param searchExpression - in case there is a search over the tree
   * @param doesOneOfAncestorsMatchSearch - boolean - is true if one of current node's ancestors matches the search expression
   * @returns { selectedNode, tree, descendantsMatchSearch }
   */
  recursivePrepareTree(
    currNode,
    selectedKey,
    ancestors,
    searchExpression,
    doesOneOfAncestorsMatchSearch
  ) {
    if (!currNode || !currNode.name) {
      return null;
    }

    const isCurrentNodeMatchSearch =
      currNode.name.toLowerCase().indexOf(searchExpression.toLowerCase()) > -1;
    const active = currNode.id === selectedKey;

    const ancestorsWithoutTempRoot =
      ancestors.length === 1 && ancestors[0].id === this.TEMP_ROOT_ID ? [] : ancestors;

    const newCurrNode = {
      name: currNode.name,
      id: currNode.id,
      ancestors: ancestorsWithoutTempRoot
    };

    if (currNode.children) {
      const newChildren = [];
      let selectedNodeFromChildren;
      let foundChildMatchSearch;

      for (let i = 0; i < currNode.children.length; i++) {
        const { selectedNode, tree, descendantsMatchSearch } = this.recursivePrepareTree(
          currNode.children[i],
          selectedKey,
          [...ancestorsWithoutTempRoot, { id: currNode.id, name: currNode.name }],
          searchExpression,
          doesOneOfAncestorsMatchSearch || isCurrentNodeMatchSearch
        );
        if (doesOneOfAncestorsMatchSearch || isCurrentNodeMatchSearch || descendantsMatchSearch) {
          newChildren.push(tree);
        }

        selectedNodeFromChildren = selectedNodeFromChildren || selectedNode;
        foundChildMatchSearch = foundChildMatchSearch || descendantsMatchSearch;
      }

      newCurrNode.children = newChildren.length > 0 ? newChildren : null;
      newCurrNode.toggled =
        (!!searchExpression && foundChildMatchSearch) ||
        (!searchExpression && !!selectedNodeFromChildren) ||
        currNode.toggled;

      return {
        selectedNode: active ? newCurrNode : selectedNodeFromChildren,
        tree: newCurrNode,
        descendantsMatchSearch: foundChildMatchSearch || isCurrentNodeMatchSearch
      };
    }

    return {
      selectedNode: active ? newCurrNode : null,
      tree: newCurrNode,
      descendantsMatchSearch: isCurrentNodeMatchSearch
    };
  }

  prepareStateWithTree(
    oldTree,
    selectedEventKey,
    expandRoot,
    searchExpression = '',
    inConstructor = false
  ) {
    let state = {};
    const errorState = { isError: true, tree: undefined, selectedNode: undefined };

    if (!oldTree) {
      state = errorState;
    } else {
      // Add a temp root node for handling a tree multiple root nodes (an example would be passing only the root's children
      // when the root should not be displayed)
      const hasSingleRoot = !Array.isArray(oldTree);
      const oldTreeAsChildNodes = !hasSingleRoot ? oldTree : [{ ...oldTree }];
      const treeWithTempRoot = {
        id: this.TEMP_ROOT_ID,
        name: this.TEMP_ROOT_ID,
        children: oldTreeAsChildNodes
      };

      if (expandRoot && hasSingleRoot) {
        treeWithTempRoot.children[0].toggled = true;
      }

      try {
        const { tree, selectedNode, descendantsMatchSearch } = this.recursivePrepareTree(
          treeWithTempRoot,
          selectedEventKey,
          [],
          searchExpression,
          false
        );
        state = { tree: descendantsMatchSearch ? tree : undefined, selectedNode, isError: false };
      } catch (err) {
        state = errorState;
        console.error('Error in prepareStateWithTree:', err);
      }
    }

    if (!inConstructor) {
      this.setState(state);
    }

    return state;
  }

  /**
   * closeMenu
   */
  closeMenu() {
    if (!this.state.isOpen) return;
    this.setState({
      isOpen: false,
      searchExpression: ''
    });
    this.props.onClose();
  }

  /**
   * openMenu
   */
  openMenu() {
    if (this.state.isOpen) return;
    this.setState({ isOpen: true });
  }

  _handleCancel() {
    this.prepareStateWithTree(
      this.props.tree,
      this.state.selectedNode && this.state.selectedNode.id,
      this.props.expandRoot
    );
    this.closeMenu();
  }

  /**
   * _handleOnFocus
   */
  _handleOnFocus() {
    this.openMenu();
  }

  /**
   * _handleOnSelect
   */
  _handleOnSelect(value) {
    this.setState({
      selectedNode: value,
      isOpen: false
    });

    this.props.onChange(
      value ? { id: value.id, name: value.name, ancestors: value.ancestors } : null
    );
  }

  _onMouseOverPopover() {
    this.setState({ isHovered: true });
  }

  _onMouseOutPopover() {
    this.setState({ isHovered: false });
  }

  /**
   * handleClickOutside
   */
  handleClickOutside() {
    if (this.state.isOpen) {
      this._handleCancel();
    }
  }

  /**
   * renderTree
   */
  renderTree() {
    return (
      <div className="DropdownTree__tree">
        <Tree
          onlyLeafSelection={this.props.onlyLeafSelection}
          onChange={this._handleOnSelect}
          onCancel={this._handleCancel}
          treeData={this.state.tree && this.state.tree.children}
          currentSelectedNode={this.state.selectedNode}
          showErrorMessage={this.state.isError || this.props.loadingError}
          isLoading={this.props.isLoading}
          onTryAgain={this.props.onTryAgain}
          clearable={this.props.clearable}
        />
      </div>
    );
  }

  _searchTree() {
    this.prepareStateWithTree(
      this.props.tree,
      this.state.selectedNode && this.state.selectedNode.id,
      this.props.expandRoot,
      this.state.searchExpression
    );
  }

  _handleSearchBoxOnChange(searchExpression) {
    this.setState({ searchExpression });
  }

  _handleClearSearch() {
    this._handleSearchBoxOnChange('');
  }

  getNodePathByName(node, excludeOwn) {
    const ancestors = node ? [...node.ancestors] : [];

    if (!excludeOwn) {
      ancestors.push({ name: node.name });
    }
    return ancestors.map(ancestor => ancestor.name);
  }

  getInputValue() {
    let inputValue = { text: '', isOverflow: false };

    if (this.state.selectedNode) {
      const ancestors = this.getNodePathByName(this.state.selectedNode);
      const computedStyle = this.readOnlyLabelElement
        ? window.getComputedStyle(this.readOnlyLabelElement)
        : {};

      const fontSize = parseInt(computedStyle.fontSize) || undefined;
      const fontWeight = parseInt(computedStyle.fontWeight) || undefined;
      const fontFamily = computedStyle.fontFamily;
      inputValue = listTruncationWithOverflow(
        ancestors,
        this.state.width,
        undefined,
        fontSize,
        fontWeight,
        fontFamily
      );
    }

    return inputValue;
  }

  renderArrow(isOpen) {
    const onClickAction = isOpen ? () => this.closeMenu() : this._handleOnFocus;
    const arrowClass = classNames('DropdownTree__arrow', {
      'DropdownTree__arrow--up': isOpen
    });

    return (
      <span
        className="DropdownTree__arrow-zone"
        onClick={this.props.disabled ? undefined : onClickAction}
      >
        <span className={arrowClass} />
      </span>
    );
  }

  getText(translationKey, customText) {
    return isNullOrUndefined(customText)
      ? this.props[TSymbolProp].translate(translationKey)
      : customText;
  }

  getPlaceholder() {
    const {
      selectedEventKey,
      isLoading,
      placeholder,
      loadingPlaceholder,
      loadingError,
      errorPlaceholder
    } = this.props;
    // Placeholder logic - if there is a selected value and we don't have the data yet or had an error - we show a placeholder as we don't know how to show it yet.
    // That's why we need to refer to the loading/error states at this point.
    // If tree is open - show regular placeholder as the menu includes loading & error indication
    if (selectedEventKey && !this.state.isOpen) {
      if (isLoading) {
        return this.getText('LOADING', loadingPlaceholder);
      } else if (loadingError) {
        return this.getText('READ_ONLY_ERROR', errorPlaceholder);
      }
    }
    return placeholder || '';
  }

  renderInput(inputValue) {
    const placeholder = inputValue ? '' : this.getPlaceholder();
    const nonOpenClasses = classNames('DropdownTree__input-text', {
      DropdownTree__placeholder: !inputValue && placeholder
    });

    const searchBoxClasses = classNames('DropdownTree__search-box', {
      'DropdownTree__search-box--disabled': this.props.disabled
    });

    return (
      <div
        className="DropdownTree__input"
        ref={node => {
          this.inputElement = node;
        }}
      >
        {!this.state.isOpen ? (
          <div className={searchBoxClasses}>
            <div
              className={nonOpenClasses}
              onClick={this.props.disabled ? undefined : this._handleOnFocus}
            >
              <span>{inputValue || placeholder}</span>
            </div>
            {this.renderArrow()}
          </div>
        ) : (
          <div className="DropdownTree__search-box-wrapper">
            <SearchBox
              searchExpression={this.state.searchExpression || ''}
              placeholder={this.props.searchPlaceholder || ''}
              onChange={this._handleSearchBoxOnChange}
              onSearch={() => {}}
              onClear={this._handleClearSearch}
              isFocused={true}
            />
            {this.renderArrow(this.state.isOpen)}
          </div>
        )}
      </div>
    );
  }

  renderReadOnlyLabel(text, isOverflow) {
    let tooltipText, isError, isEmptyValue, errorTooltipText;
    const {
      tree,
      isLoading,
      selectedEventKey,
      readOnlyErrorPlaceholder,
      readOnlyErrorTooltipText,
      readOnlyEmptyValuePlaceholder,
      loadingPlaceholder
    } = this.props;

    if (!tree && selectedEventKey) {
      if (isLoading) {
        text = this.getText('LOADING', loadingPlaceholder);
        tooltipText = text;
        isEmptyValue = true;
      } else {
        text = this.getText('READ_ONLY_ERROR', readOnlyErrorPlaceholder);
        errorTooltipText = this.getText('READ_ONLY_ERROR_TOOLTIP', readOnlyErrorTooltipText);
        isError = true;
      }
    } else if (!this.state.selectedNode || !text) {
      text = this.getText('READ_ONLY_EMPTY_VALUE', readOnlyEmptyValuePlaceholder);
      tooltipText = text;
      isEmptyValue = true;
    } else {
      const ancestors = this.getNodePathByName(this.state.selectedNode);
      tooltipText = listTruncationWithOverflow(ancestors).text;
    }

    const spanClasses = classNames('DropdownTree__read-only-label', {
      'DropdownTree__read-only-label--no-value': isEmptyValue || isError
    });

    return (
      <Tooltip
        container={this.props.popoverContainer}
        tooltipClassName={isError ? 'DropdownTree__read-only-tooltip--error' : undefined}
        place={isError ? Tooltip.Places.TOP : Tooltip.Places.BOTTOM}
        show={this.state.isHovered && !isEmptyValue && (isOverflow || tooltipText !== text)}
        content={isError ? errorTooltipText : tooltipText}
      >
        <span
          className={spanClasses}
          onMouseOver={this._onMouseOverPopover}
          onMouseOut={this._onMouseOutPopover}
          ref={node => {
            this.readOnlyLabelElement = node;
          }}
        >
          {text}
        </span>
      </Tooltip>
    );
  }

  /**
   * render
   */
  render() {
    const cn = classNames(
      'DropdownTree',
      {
        'DropdownTree--focused': this.state.isOpen,
        'DropdownTree--opened': this.state.isOpen
      },
      this.props.className
    );

    const { text, isOverflow } = this.getInputValue();

    return (
      <div className={cn}>
        {this.props.readOnlyValue
          ? this.renderReadOnlyLabel(text, isOverflow)
          : this.renderInput(text)}
        {this.state.isOpen && this.renderTree()}
      </div>
    );
  }
}

DropdownTree.defaultProps = {
  expandRoot: true,
  openOnMount: false,
  onlyLeafSelection: false,
  clearable: true,
  onChange: noop,
  onClose: noop
};

const treeNodeShape = PropTypes.shape({
  /**
   * Tree node id
   */
  id: PropTypes.string.isRequired,
  /**
   * Tree node display name
   */
  name: PropTypes.string.isRequired,
  /**
   * Array of tree node children
   */
  children: PropTypes.array
});

DropdownTree.propTypes = {
  /**
   * Selected Key - the id of the selected node in the tree
   */
  selectedEventKey: PropTypes.string,
  /**
   * A place holder for selected text
   */
  placeholder: PropTypes.string,
  /**
   * A place holder for search input
   */
  searchPlaceholder: PropTypes.string,
  /**
   * A callback function for on change event
   */
  onChange: PropTypes.func,
  /**
   * A callback function for on close event
   */
  onClose: PropTypes.func,
  /**
   * The tree data (could be either a single root or an array of root nodes)
   */
  tree: PropTypes.oneOfType([PropTypes.arrayOf(treeNodeShape), treeNodeShape]),
  /**
   * A custom className for the container element of the component
   */
  className: PropTypes.string,
  /**
   * Expands the root and displays its children on first render
   */
  expandRoot: PropTypes.bool,
  /**
   * Whether the selected value should be displayed as read only
   */
  readOnlyValue: PropTypes.bool,
  /**
   * Language locale
   */
  locale: PropTypes.string,
  /**
   * A placeholder when there's no value in read only mode
   */
  readOnlyEmptyValuePlaceholder: PropTypes.string,
  /**
   * A placeholder in read only mode when tree wasn't passed to the component
   */
  readOnlyErrorPlaceholder: PropTypes.string,
  /**
   * A placeholder to show when tree wasn't passed to the component & still is loading
   */
  loadingPlaceholder: PropTypes.string,
  /**
   * A placeholder to show when tree wasn't passed to the component
   */
  errorPlaceholder: PropTypes.string,
  /**
   * The text for the tooltip of the error value when in read only mode
   */
  readOnlyErrorTooltipText: PropTypes.string,
  /**
   * Whether to open the dropdown on mount
   */
  openOnMount: PropTypes.bool,
  /**
   * Allow selecting only leaf nodes.
   */
  onlyLeafSelection: PropTypes.bool,
  /**
   * An optional width for the value truncation calculation
   */
  availableWidth: PropTypes.number,
  /**
   * Whether component should render in disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * An optional function to be ran when menu get closed or click outside
   */
  onClickOutside: PropTypes.func,
  /**
   * Whether locations are still loading
   */
  isLoading: PropTypes.bool,
  /**
   * Whether there was an error loading locations
   */
  loadingError: PropTypes.bool,
  /**
   * A Node, Component instance, or function that returns either. The `container` will have the Tooltip appended to it.
   */
  popoverContainer: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * Handle try again click on loading error
   */
  onTryAgain: PropTypes.func,
  /**
   * Enable / Disable clearing.
   */
  clearable: PropTypes.bool
};

export default withLocale(
  DropdownTree,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
